import React from 'react';
import gql from 'graphql-tag';
import {useQuery} from "@apollo/client";
import styled from 'styled-components';
import {Link} from "react-router-dom";
import {Card, Tooltip} from "@blueprintjs/core";
import * as Classes from "@blueprintjs/core/lib/cjs/common/classes";
import {Pane, Spinner, Text} from "evergreen-ui";

const STANDINGS_QUERY = gql`
    query($division: Int!) {
        teamsByDivision(divisionId: $division) {
            id
            name
            displayName: name
            division {
                id
                name
            }

            isCommittedToPlayoffs
            
            record
            runsFor
            runsAgainst
            leaguePoints
            leagueSeed
            
            currentStreak
            expectedRecord
            winPercentage
            recordOverLastTenGames
            percentageOfMaxPointsEarned
        }
    }
`

export const StyledCard = styled(Card)`
  margin: 10px 0;
  padding: 10px 0 0 0;
  width: 100%;
`

const StyledGrid = styled.table`
  overflow-x: scroll;
  min-width: 775px;
  width: 100%;
  border-collapse: collapse;
`

export const StandingsView = ({ division }) => {
  const { data } = useQuery(STANDINGS_QUERY, {
    variables: {
      division: division.value
    }
  })

  if (!data) return (
    <Pane display={'flex'} justifyContent={'center'} flexDirection={'column'} alignItems={'center'}>
      <Spinner size={48} />
      <Text>Crunching the latest numbers...</Text>
    </Pane>
  )

  const { teamsByDivision: teams } = data

  let parsed = teams.reduce((acc, curr) => {
    const { name } = curr.division

    if (acc[name] === undefined) {
      acc[name] = [curr]
    } else {
      acc[name].push(curr)
    }

    return acc
  }, {})

  const blocks = Object.keys(parsed)

  return (
    <>
      {
       blocks.map(division => {
         return <StandingsPane
           key={division}
           division={division}
           teams={parsed[division].sort((a,b) => {
             // const aGp = a.record.split("-").map(p => parseInt(p)).reduce((acc, curr) => acc + curr)
             // const bGp = b.record.split("-").map(p => parseInt(p)).reduce((acc, curr) => acc + curr)

             // const aRunsAgainstAverage = aGp ? (a.runsAgainst / aGp) : 1000
             // const bRunsAgainstAverage = bGp ? (b.runsAgainst / bGp) : 1000

             return (b.leaguePoints ?? 0) - (a.leaguePoints ?? 0) || (a.leagueSeed || 1000) - (b.leagueSeed || 1000)//aRunsAgainstAverage - bRunsAgainstAverage
           })}
         />
       })
      }

      <StyledCard style={{padding: '10px'}}>
        <p style={{margin: 0, padding: 0}}><strong>x</strong>&nbsp;-&nbsp;Clinched Playoff Berth (6 decisions for Spring/Fall, 8 decisions for Summer)</p>
        <p style={{margin: 0, padding: 0}}><strong>i</strong>&nbsp;-&nbsp;Ineligible for more points (first 12 decisions earn points for playoff seeding)</p>
        <div style={{height: '10px'}} />

        <UnsubmittedPlayoffFormKeyIcon />
        <OptedOutOfPlayoffFormKeyIcon />
      </StyledCard>
    </>
  )
}

const StandingsPane = ({ division, teams }) => {
  return (
    <StyledCard>
      <h2 style={{margin: 0, padding: 0, paddingLeft: 8, marginBottom: '10px', fontWeight: '500', position: 'sticky', left: 0}}>{division} Teams</h2>
      <div
        style={{width: '100%', overflowX: 'auto', overflowY: 'hidden'}}
      >
      <StyledGrid>
        <thead style={{backgroundColor: '#0A7EC2', color: 'white'}}>
          <tr>
            <th
              style={{
                borderRadius: '3px 0 0 0',
                fontWeight: '500',
                textAlign: 'left',
                position: 'sticky',
                left: 0,
                zIndex: 15,
                backgroundColor: '#0A7EC2',
                borderBottom: '1px solid #ddd',
                minWidth: '125px',
                padding: '10px'
              }}
            >
              Team
            </th>

            <CenteredTableHeader
              style={{minWidth: '25px', borderBottom: '1px solid #ddd'}}
            >
              <Tooltip
                className={Classes.TOOLTIP_INDICATOR}
                content={<p style={{fontWeight: '500', margin: 0, padding: 0}}>Wins</p>}
              >
                <span style={{fontWeight: '500'}}>W</span>
              </Tooltip>
            </CenteredTableHeader>

            <CenteredTableHeader
              style={{minWidth: '25px', borderBottom: '1px solid #ddd'}}
            >
              <Tooltip
                className={Classes.TOOLTIP_INDICATOR}
                content={<p style={{fontWeight: '500', margin: 0, padding: 0}}>Losses</p>}
              >
                <span style={{fontWeight: '500'}}>L</span>
              </Tooltip>
            </CenteredTableHeader>

            <CenteredTableHeader
              style={{minWidth: '25px', borderBottom: '1px solid #ddd'}}
            >
              <Tooltip
                className={Classes.TOOLTIP_INDICATOR}
                content={<p style={{fontWeight: '500', margin: 0, padding: 0}}>Ties</p>}
              >
                <span style={{fontWeight: '500'}}>T</span>
              </Tooltip>
            </CenteredTableHeader>

            <CenteredTableHeader
              style={{minWidth: '25px', borderBottom: '1px solid #ddd'}}
            >
              <Tooltip
                className={Classes.TOOLTIP_INDICATOR}
                content={<p style={{fontWeight: '500', margin: 0, padding: 0}}>Win Percentage (ties count as half a win)</p>}
              >
                <span style={{fontWeight: '500'}}>W%</span>
              </Tooltip>
            </CenteredTableHeader>

            <CenteredTableHeader
              style={{minWidth: '25px', borderRight: '1px solid #ddd', borderBottom: '1px solid #ddd'}}
            >
              <Tooltip
                className={Classes.TOOLTIP_INDICATOR}
                content={<p style={{fontWeight: '500', margin: 0, padding: 0}}>Point Percentage. The ratio of points earned to maximum possible points.</p>}
              >
                <span style={{fontWeight: '500'}}>P%</span>
              </Tooltip>
            </CenteredTableHeader>


            <CenteredTableHeader
              style={{minWidth: '50px', borderLeft: '1px solid #ddd', borderBottom: '1px solid #ddd'}}
            >
              <Tooltip
                className={Classes.TOOLTIP_INDICATOR}
                content={<p style={{fontWeight: '500', margin: 0, padding: 0}}>Record in the last 5 games</p>}
              >
                <span style={{fontWeight: '500'}}>L5</span>
              </Tooltip>
            </CenteredTableHeader>

            <CenteredTableHeader
              style={{minWidth: '25px', borderRight: '1px solid #ddd', borderBottom: '1px solid #ddd'}}
            >
              <Tooltip
                className={Classes.TOOLTIP_INDICATOR}
                content={<p style={{fontWeight: '500', margin: 0, padding: 0}}>Current Streak</p>}
              >
                <span style={{fontWeight: '500'}}>STRK</span>
              </Tooltip>
            </CenteredTableHeader>


            <CenteredTableHeader
              style={{width: '35px', borderBottom: '1px solid #ddd'}}
            >
              <Tooltip
                className={Classes.TOOLTIP_INDICATOR}
                content={<p style={{fontWeight: '500', margin: 0, padding: 0}}>Runs Scored</p>}
              >
                <span style={{fontWeight: '500'}}>RS</span>
              </Tooltip>
            </CenteredTableHeader>

            <CenteredTableHeader
              style={{width: '35px', borderBottom: '1px solid #ddd'}}
            >
              <Tooltip
                className={Classes.TOOLTIP_INDICATOR}
                content={<p style={{fontWeight: '500', margin: 0, padding: 0}}>Runs Allowed</p>}
              >
                <span style={{fontWeight: '500'}}>RA</span>
              </Tooltip>
            </CenteredTableHeader>

            <CenteredTableHeader
              style={{width: '35px', borderBottom: '1px solid #ddd', fontWeight: '500'}}
            >
              <Tooltip
                className={Classes.TOOLTIP_INDICATOR}
                content={<p style={{fontWeight: '500', margin: 0, padding: 0}}>Run Differential</p>}
              >
                <span style={{fontWeight: '500'}}>DIFF</span>
              </Tooltip>
            </CenteredTableHeader>

            <CenteredTableHeader
              style={{width: '35px', borderBottom: '1px solid #ddd', fontWeight: '500'}}
            >
              <Tooltip
                className={Classes.TOOLTIP_INDICATOR}
                content={<p style={{fontWeight: '500', margin: 0, padding: 0}}>Runs Against Average</p>}
              >
                <span style={{fontWeight: '500'}}>RAA</span>
              </Tooltip>
            </CenteredTableHeader>

            <CenteredTableHeader
              style={{width: '75px', borderBottom: '1px solid #ddd', borderRight: '1px solid #ddd'}}
            >
              <Tooltip
                className={Classes.TOOLTIP_INDICATOR}
                content={<p style={{fontWeight: '500', margin: 0, padding: 0}}>Expected win/loss record based on runs scored and runs allowed</p>}
              >
                <span style={{fontWeight: '500'}}>X-W/L</span>
              </Tooltip>
            </CenteredTableHeader>


            <CenteredTableHeader
              style={{width: '50px', borderBottom: '1px solid #ddd', fontWeight: '500', borderRight: '1px solid #ddd'}}
            >
              <Tooltip
                className={Classes.TOOLTIP_INDICATOR}
                content={<p style={{fontWeight: '500', margin: 0, padding: 0}}>League Points</p>}
              >
                <span style={{fontWeight: '500'}}>POINTS</span>
              </Tooltip>
            </CenteredTableHeader>

            <CenteredTableHeader
              style={{width: '50px', borderBottom: '1px solid #ddd', fontWeight: '500', borderRadius: '0 3px 0 0'}}
            >
              <Tooltip
                className={Classes.TOOLTIP_INDICATOR}
                content={<p style={{fontWeight: '500', margin: 0, padding: 0}}>Playoff seed. A seed is only assigned to teams who have committed to the playoffs and played at least 6 games.</p>}
              >
                <span style={{fontWeight: '500'}}>SEED</span>
              </Tooltip>
            </CenteredTableHeader>

          </tr>
        </thead>

        <tbody>
          {[...teams]
            .map(team => <TeamStandingsItem key={team.id} {...team} />)}
        </tbody>
      </StyledGrid>
      </div>
    </StyledCard>
  )
}


const TeamStandingsItemWrapper = styled.tr`
  height: 40px;

  border-bottom: 1px solid lightgrey;

  :hover {
    background-color: rgba(0,0,0,0.03);
  }
`

const CenteredTableData = styled.td`
  position: relative;
  text-align: center;
  font-weight: 500;
`

const CenteredTableHeader = styled.th`
  text-align: center;
`

const UnstyledLink = styled(Link)`
  font-weight: 500;
  margin-left: 3px;
  display: inline-block;
  
  :link {
    cursor: pointer;
    text-decoration: inherit;
    color: inherit;
  }

  :visited {
    cursor: pointer;
    text-decoration: inherit;
    color: inherit;
  }
`

const TeamStandingsItem = (team) => {

  const {
    id,
    name,
    runsFor,
    runsAgainst,
    leaguePoints,
    leagueSeed,
    record,
    currentStreak,
    expectedRecord,
    winPercentage,
    recordOverLastTenGames,
    percentageOfMaxPointsEarned,
    isCommittedToPlayoffs
  } = team

  const [wins, losses, ties] = record.split("-")

  const formattedWinPercentage = formatDecimal(winPercentage)
  const formattedPercentageOfMaxPointsEarned = formatDecimal(percentageOfMaxPointsEarned)

  const gamesPlayed = (parseInt(wins) + parseInt(losses) + parseInt(ties))
  const runsAgainstAverage = gamesPlayed ? (Math.round((runsAgainst / gamesPlayed) * 100) / 100).toFixed(1) : '-'

  return (
    <TeamStandingsItemWrapper>
      <td style={{textAlign: 'left', position: 'sticky', left: 0, backgroundColor: '#efefef', zIndex: 20, width: '125px', borderLeft: '1px solid #ddd', borderRight: '2px solid #ddd'}}>
        <UnstyledLink
          to={`/home?teamId=${id}`}
        >
          {name}{gamesPlayed >= 6 && <span style={{verticalAlign: 'super', fontWeight: '500', color: '#43BF4D'}}>&nbsp;x</span>}{gamesPlayed >= 16 && <span style={{color: '#43BF4D', verticalAlign: 'super', fontWeight: '500'}}>&nbsp;i</span>}
        </UnstyledLink>
      </td>

      <CenteredTableData
        style={{width: '25px'}}
      >{wins}</CenteredTableData>
      <CenteredTableData
        style={{width: '25px'}}
      >{losses}</CenteredTableData>
      <CenteredTableData
        style={{width: '25px'}}
      >{ties}</CenteredTableData>
      <CenteredTableData
        style={{width: '25px'}}
      >{formattedWinPercentage}</CenteredTableData>
      <CenteredTableData
        style={{width: '25px', borderRight: '1px solid #ddd'}}
      >{formattedPercentageOfMaxPointsEarned}</CenteredTableData>

      <CenteredTableData
        style={{width: '50px'}}
      >{recordOverLastTenGames}</CenteredTableData>
      <CenteredTableData
        style={{width: '25px', borderRight: '1px solid #ddd'}}
      >{currentStreak}</CenteredTableData>

      <CenteredTableData>{runsFor}</CenteredTableData>
      <CenteredTableData>{runsAgainst}</CenteredTableData>
      <RunDiffCell diff={runsFor - runsAgainst} />
      <CenteredTableData>{runsAgainstAverage}</CenteredTableData>

      <CenteredTableData style={{borderRight: '1px solid #ddd'}}>{expectedRecord}</CenteredTableData>

      <RequiredInPlayoffsCell isCommittedToPlayoffs={isCommittedToPlayoffs} style={{borderRight: '1px solid #ddd'}}>
        {leaguePoints}
      </RequiredInPlayoffsCell>

      <RequiredInPlayoffsCell isCommittedToPlayoffs={isCommittedToPlayoffs} style={{fontWeight: '600', borderRight: '0px solid #ddd'}}>
        {leagueSeed ?? '-'}
      </RequiredInPlayoffsCell>

    </TeamStandingsItemWrapper>
  )
}

const RequiredInPlayoffsCell = ({ isCommittedToPlayoffs, children, style, ...rest }) => {
  return (
    <>
        <CenteredTableData style={{...style, fontWeight: '600'}} {...rest}>
          {isCommittedToPlayoffs &&
            <>{children}</>
          }
          {!isCommittedToPlayoffs &&
            <LinearGradientCard isCommittedToPlayoffs={isCommittedToPlayoffs}>
              {children}
            </LinearGradientCard>
          }
        </CenteredTableData>
    </>
  )
}

const LinearGradientCard = styled.div`
  ${props => {
    if (props.isCommittedToPlayoffs === null) {
      return `
        background:
          repeating-linear-gradient(
            -45deg,
            #FFB366,
            #FFB366 4px,
            #FFB366a0 4px,
            #FFB366a0 8px
          )
      `
    } else {
      return `
        background-color: lightgrey
        `
    }
  }};
    
  position: absolute;
  left: 1px;
  right: 1px;
  bottom: 1px;
  top: 1px;
  font-weight: 600;
  display: flex;
  flex-direction: column;
  justify-content: center;
`

function formatDecimal(number) {
  let formattedWinPercentage = number ?? '-'
  if (number !== null) {
    formattedWinPercentage = `${formattedWinPercentage}`.substring(1)

    if (number === 0) {
      formattedWinPercentage = '.000'
    } else if (number === 1) {
      formattedWinPercentage = '1.000'
    }

    const length = formattedWinPercentage.length

    if (length < 4) {
      formattedWinPercentage = `${formattedWinPercentage}${Array(4 - length + 1).join('0')}`
    }
  }

  return formattedWinPercentage
}

const RunDiffCell = ({ diff }) => {

  let modifier = ''
  let color;

  if (diff > 0) {
    modifier = '+'
    color = '#238C2C'
  } else if (diff < 0) {
    color = '#F55656'
  }

  return <CenteredTableData style={{color}}>{modifier}{diff}</CenteredTableData>
}

function UnsubmittedPlayoffFormKeyIcon() {
  return (
    <div style={{display: 'flex'}}>
      <div
        style={{
          width: '20px', height: '20px',
          border: '3px solid #fff',
          borderRadius: '6px',
          marginRight: '5px',
          background: `
            repeating-linear-gradient(
              -45deg,
              #FFB366,
              #FFB366 4px,
              #FFB366a0 4px,
              #FFB366a0 8px
            )
          `
        }}
      />
      <p style={{margin: 0, padding: 0}}>Team has not submitted playoff form</p>
    </div>
  )
}

function OptedOutOfPlayoffFormKeyIcon() {
  return (
    <div style={{display: 'flex'}}>
      <div
        style={{
          width: '20px', height: '20px',
          border: '3px solid #fff',
          borderRadius: '6px',
          marginRight: '5px',
          backgroundColor: 'lightgrey'
        }}
      />
      <p style={{margin: 0, padding: 0}}>Team has opted out of playoffs</p>
    </div>
  )
}
